import { css, html, LitElement, render } from "lit";
import "@vaadin/dialog";
import "@vaadin/button";
import "@vaadin/text-field";
import "@vaadin/password-field";
import "@vaadin/checkbox";
import { getPinValidationError } from "../../lib/PinValidator.js";
import { resolveAuthI18n } from "./authI18n.js";
import { hasValidLoginDetails, canSubmitAuth } from "./authValidation.js";
import "./AuthFormContent.js";

export default class AuthForm extends LitElement {
    static properties = {
        dialogOpened: { type: Boolean }, i18n: { attribute: false }, loginError: { type: Boolean },
        savedCredentials: { type: Boolean }, rememberAvailable: { type: Boolean },
        authorize: { attribute: false },
        mode: { state: true }, remember: { state: true }, busy: { state: true }, error: { state: true },
        customerNumber: { state: true }, user: { state: true }, password: { state: true },
        pinError: { state: true }, pin: { state: true }, confirmPin: { state: true }
    };

    constructor() {
        super();
        this.dialogOpened = false;
        this.savedCredentials = false;
        this.rememberAvailable = false;
        this.loginError = false;
        this.mode = "credentials";
        this.remember = false;
        this.busy = false;
        this.error = "";
        this.clearFields();
        this.onPageHide = () => this.clearFields();
    }

    static styles = css`:host { display: contents; }`;

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener("pagehide", this.onPageHide);
    }

    disconnectedCallback() {
        this.clearFields();
        window.removeEventListener("pagehide", this.onPageHide);
        super.disconnectedCallback();
    }

    clearFields() {
        this.customerNumber = this.user = this.password = this.pin = this.confirmPin = "";
        this.pinError = "";
        this.remember = false;
    }

    willUpdate(changed) {
        if (changed.has("savedCredentials")) this.mode = this.savedCredentials ? "pin" : "credentials";
        if (changed.has("loginError") && this.loginError) this.mode = "credentials";
    }

    updated() {
        this.renderRoot.querySelector("vaadin-dialog")?.requestContentUpdate();
    }

    renderDialogContent = (root) => {
        render(html`<dat-auth-form-content .content=${this.dialogContent()}></dat-auth-form-content>`, root);
    };

    switchMode() {
        this.clearFields();
        this.error = "";
        this.mode = this.mode === "pin" ? "credentials" : "pin";
    }

    async resolve() {
        if (this.busy || !canSubmitAuth(this)) return;
        this.error = "";
        const text = resolveAuthI18n(this.i18n);
        this.busy = true;
        try {
            await this.authorize({ mode: this.mode, pin: this.mode === "pin" || this.remember ? this.pin : null,
                credentials: this.mode === "credentials" ? {
                    customerNumber: this.customerNumber, user: this.user.trim(), password: this.password
                } : null });
            this.clearFields();
        } catch (error) {
            if (error.name === "AbortError") return;
            this.error = text.errorMessages[error.message] ?? text.errorMessages.unexpected ?? "";
            this.pin = this.confirmPin = "";
            this.pinError = "";
        } finally {
            this.busy = false;
        }
    }

    validatePin(checkConfirmation = false) {
        this.pinError = getPinValidationError(this.pin, this.mode === "credentials");
        if (!this.pinError && this.mode === "credentials" &&
            (checkConfirmation || this.confirmPin) && this.pin !== this.confirmPin) {
            this.pinError = "pinMismatch";
        }
    }

    updateLoginField(property, value) {
        this[property] = value;
    }

    clearAuthError() {
        this.error = "";
        this.loginError = false;
    }

    pinField(label, property) {
        const text = resolveAuthI18n(this.i18n);
        const fieldError = property === "confirmPin"
            ? (this.pinError === "pinMismatch" ? text.errorMessages.pinMismatch : "")
            : (this.pinError !== "pinMismatch" ? text.errorMessages[this.pinError] : "") ||
                (this.mode === "pin" ? this.error : "");
        return html`<vaadin-password-field required manual-validation reveal-button-hidden
            label=${label} inputmode="numeric" pattern="[0-9]{5}" minlength="5" maxlength="5"
            autocomplete="off" .value=${this[property]}
            ?disabled=${this.busy || (this.mode === "credentials" && !hasValidLoginDetails(this))}
            .invalid=${Boolean(fieldError)} .errorMessage=${fieldError || text.errorMessages.pinLength}
            @input=${() => this.clearAuthError()}
            @blur=${() => this.validatePin(property === "confirmPin")}
            @value-changed=${(event) => {
                this[property] = event.detail.value;
                if (this.pinError || this.pin.length === 5) this.validatePin();
            }}></vaadin-password-field>`;
    }

    dialogContent() {
        const text = resolveAuthI18n(this.i18n);
        return html`
            <style>
                .auth-form-content { width: 100%; min-width: 0; box-sizing: border-box; display: flex; flex-direction: column; gap: 8px; }
                .auth-form-content h2 { font-size: var(--lumo-font-size-m); font-weight: 600; line-height: var(--lumo-line-height-s); margin: 0 0 4px; overflow-wrap: anywhere; white-space: normal; }
                .auth-form-content vaadin-text-field, .auth-form-content vaadin-password-field { width: 100%; }
                .auth-form-content vaadin-checkbox { padding-block: 0; }
                .auth-form-content .pin-fields { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 12px; }
                .auth-form-content .actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 8px; margin-top: 4px; }
                .auth-form-content .actions vaadin-button { flex-shrink: 0; max-width: 100%; height: auto; min-height: var(--lumo-size-m); margin: 0; }
                .auth-form-content .actions .switch-mode { flex-shrink: 1; min-width: 0; white-space: normal; height: auto; min-height: var(--lumo-size-m); font-size: var(--lumo-font-size-s); line-height: var(--lumo-line-height-s); }
                .auth-form-content .action-label { display: block; white-space: normal; overflow-wrap: anywhere; line-height: var(--lumo-line-height-s); }
            </style>
            <div class="auth-form-content" @keydown=${(event) => {
                const input = event.composedPath()[0];
                if (event.key === "Enter" && input.tagName === "INPUT" && input.type !== "checkbox") {
                    event.preventDefault();
                    this.resolve();
                }
            }}>
                <h2>${this.mode === "pin" ? text.pinTitle : text.title}</h2>
                ${this.mode === "pin" ? this.pinField(text.pin, "pin") : html`
                    <vaadin-text-field required label=${text.customerNumber} pattern="[0-9]{7}"
                        error-message=${text.errorMessages.customerNumber} autocomplete="off"
                        .value=${this.customerNumber} ?disabled=${this.busy}
                        @input=${() => this.clearAuthError()}
                        @value-changed=${(event) => { this.updateLoginField("customerNumber", event.detail.value); }}></vaadin-text-field>
                    <vaadin-text-field required label=${text.user} error-message=${text.errorMessages.user} autocomplete="off"
                        .value=${this.user} ?disabled=${this.busy}
                        @input=${() => this.clearAuthError()}
                        @value-changed=${(event) => { this.updateLoginField("user", event.detail.value); }}></vaadin-text-field>
                    <vaadin-password-field required label=${text.password} .invalid=${Boolean(this.error || this.loginError)}
                        .errorMessage=${this.error || (this.loginError ? text.errorMessages.errorInvalidCredentials : text.errorMessages.password)}
                        autocomplete="off" reveal-button-hidden .value=${this.password} ?disabled=${this.busy}
                        @input=${() => this.clearAuthError()}
                        @value-changed=${(event) => { this.updateLoginField("password", event.detail.value); }}></vaadin-password-field>
                    ${this.rememberAvailable ? html`
                        <vaadin-checkbox .label=${text.remember} .checked=${this.remember} ?disabled=${this.busy || !hasValidLoginDetails(this)}
                            @checked-changed=${(event) => {
                                this.remember = event.detail.value;
                                if (!this.remember) { this.pin = this.confirmPin = ""; this.pinError = ""; }
                            }}></vaadin-checkbox>
                        ${this.remember ? html`<div class="pin-fields">${this.pinField(text.pin, "pin")}${this.pinField(text.confirmPin, "confirmPin")}</div>` : ""}
                    ` : ""}
                `}
                <div class="actions">
                    ${this.savedCredentials ? html`<vaadin-button class="switch-mode" theme="tertiary" ?disabled=${this.busy} @click=${() => this.switchMode()}>
                        <span class="action-label">${this.mode === "pin" ? text.useCredentials : text.usePin}</span>
                    </vaadin-button>` : ""}
                    <vaadin-button theme="primary" ?disabled=${this.busy || !canSubmitAuth(this)} @click=${() => this.resolve()}>
                        <span class="action-label">${this.busy ? text.working : this.mode === "pin" ? text.unlockButton : text.loginButton}</span>
                    </vaadin-button>
                </div>
            </div>`;
    }

    render() {
        const text = resolveAuthI18n(this.i18n);
        return html`<vaadin-dialog no-close-on-outside-click no-close-on-esc
            .width=${this.mode === "pin" ? "min(316px, calc(100vw - 32px))" : "min(348px, calc(100vw - 32px))"}
            aria-label=${this.mode === "pin" ? text.pinTitle : text.title} .opened=${this.dialogOpened}
            .renderer=${this.renderDialogContent}>
        </vaadin-dialog>`;
    }
}

customElements.define("dat-auth-form", AuthForm);
